import React, { useEffect } from 'react';
import { 
  Alert, 
  Snackbar, 
  IconButton, 
  Box
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { 
  selectAllMessages, 
  removeMessage 
} from '../redux/alerts';
import { useAppDispatch, useAppSelector } from '../redux/store';

interface AlertDisplayProps {
  maxMessages?: number;
  autoDismiss?: boolean;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

const AlertDisplay: React.FC<AlertDisplayProps> = ({
  maxMessages = 5,
  autoDismiss = true,
  position = 'top-right'
}) => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectAllMessages);

  // Auto-dismiss messages
  useEffect(() => {
    if (!autoDismiss) return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    messages.forEach((message) => {
      if (message.autoDismiss && message.dismissAfter) {
        const timer = setTimeout(() => {
          dispatch(removeMessage(message.id));
        }, message.dismissAfter);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [messages, dispatch, autoDismiss]);

  const handleClose = (messageId: string) => {
    dispatch(removeMessage(messageId));
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top-right':
        return { top: 16, right: 16 };
      case 'top-left':
        return { top: 16, left: 16 };
      case 'bottom-right':
        return { bottom: 16, right: 16 };
      case 'bottom-left':
        return { bottom: 16, left: 16 };
      case 'top-center':
        return { top: 16, left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-center':
        return { bottom: 16, left: '50%', transform: 'translateX(-50%)' };
      default:
        return { top: 16, right: 16 };
    }
  };

  const displayMessages = messages.slice(0, maxMessages);

  if (displayMessages.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: 9999,
        ...getPositionStyles(),
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 400,
        width: '100%'
      }}
    >
      {displayMessages.map((message) => (
        <Snackbar
          key={message.id}
          open={true}
          anchorOrigin={{
            vertical: position.includes('top') ? 'top' : 'bottom',
            horizontal: position.includes('right') ? 'right' : position.includes('left') ? 'left' : 'center'
          }}
          sx={{ position: 'static', transform: 'none' }}
        >
          <Alert
            severity={message.type}
            action={
              message.dismissible && (
                <IconButton
                  size="small"
                  onClick={() => handleClose(message.id)}
                  sx={{ color: 'inherit' }}
                >
                  <CloseIcon />
                </IconButton>
              )
            }
            sx={{ width: '100%' }}
          >
            {message.message}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
};

export default AlertDisplay;
